import { useCallback, useEffect, useState } from 'react';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import type { ChangeLogResponse } from '../types/changelog';

interface UseChangeLogReturn {
  response: ChangeLogResponse | null;
  loading: boolean;
  error: ReviewError | null;
  refetch: () => void;
}

/**
 * Changelog list for one row. Fetches whenever `resource` or `id`
 * change. Idle when either is undefined so the drawer can render
 * without opening a stray request while the panel is closed.
 *
 * Uses the mounted-flag cleanup pattern — see `useReviewableResources`.
 */
export function useChangeLog(
  resource: string | undefined,
  id: number | string | undefined,
): UseChangeLogReturn {
  const [response, setResponse] = useState<ChangeLogResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(
    Boolean(resource) && id !== undefined && id !== null && id !== '',
  );
  const [error, setError] = useState<ReviewError | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!resource || id === undefined || id === null || id === '') {
      setResponse(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    reviewService
      .getChangeLog(resource, id)
      .then((data) => {
        if (cancelled) return;
        // Diagnostic: shows what the server actually returned after
        // normalisation. If `entries: 0` shows up even after a save
        // that should have written an audit row, the shape is off in
        // some way the extractor didn't catch — paste the log and
        // I'll add the alias.
        // eslint-disable-next-line no-console
        console.info('[review] changelog →', { resource, id }, {
          entries: data.data.length,
          original_restore_available: data.original_restore_available,
        });
        setResponse(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[review] changelog failed', err);
        setError(toReviewError(err));
        setResponse(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resource, id, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { response, loading, error, refetch };
}
